import * as React from 'react';
import debug from 'debug';
import {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {clearInterval, setInterval} from 'worker-timers';
import {
  MOBILE_ERROR_TYPE,
  PAYMENT_STATUS, POLLING_TIME,
  POST_MESSAGE_TYPE,
  TRANSACTION_STATUS
} from '../../common/constants';
import {getPaymentCredentials, getTransactionResult, updateTransactionStatus} from '../../libs/GsmpiApi';
import {postMessageToParent} from '../../utils/postMessage';
import {useSelector} from 'react-redux';
import SamsungPayMobileBox from '../../components/common/SamsungPayMobileBox';
import classNames from 'classnames/bind';
import styles from '../../css/SamsungPayMobileBox.module.css';
import {useTranslation} from 'react-i18next';
import i18n from '../../i18n/i18n';

const cx = classNames.bind(styles);
const log = debug("app:WebViewBridgePage");

export default function WebViewBridgePage() {
  const [statusUpdateCountDown, setStatusUpdateCountDown] = useState(null);
  const [statusPollingCountDown, setStatusPollingCountDown] = useState(0);
  const {t} = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const transactionId = queryParams.get('transactionId');
  const serviceId = queryParams.get('serviceId');
  const {sourceOrigin, merchantName} = useSelector((state) => state.paymentClient);
  const [isDisabled, setIsDisabled] = useState(false);
  const abortControllersRef = useRef([]);

  useEffect(() => {
    log(`transactionId: ${transactionId}, serviceId: ${serviceId}`);
    if (transactionId && serviceId && transactionId !== '' && serviceId !== '' && !isDisabled) {
      const iframe = document.createElement('iframe');
      iframe.id = 'app_call_frame';
      iframe.style.display = 'none';
      iframe.src = getIntentUrl();
      document.body.appendChild(iframe);

      log(`Set polling time was done`);
      setStatusPollingCountDown(POLLING_TIME.MOBILE_POLL);
      setStatusUpdateCountDown(POLLING_TIME.MOBILE_POLL);
      setIsDisabled(true);
      return () => {
        document.body.removeChild(iframe);
      };
    }
  }, [transactionId, serviceId]);

  const handleTransactionResult = (result) => {
    switch (result.status) {
    case TRANSACTION_STATUS.EXPIRED:
      navigate(`/gsmpi-web/webview/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.PAYMENT_TIMEOUT}});
      break;
    case TRANSACTION_STATUS.CANCELED:
      navigate(`/gsmpi-web/webview/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.USER_CANCEL}});
      break;
    case TRANSACTION_STATUS.ERRED:
      navigate(`/gsmpi-web/webview/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.OTHER_ERRORS}});
      break;
    case TRANSACTION_STATUS.APP_UPDATE_REQUIRED:
      navigate(`/gsmpi-web/webview/${i18n.language}/update`);
      break;
    default:
      log(`result status : ${result.status}`);
    }
  };

  useEffect(() => {
    if (statusUpdateCountDown !== null) {
      if (statusUpdateCountDown > 0) {
        const timerInterval = setInterval(() => {
          setStatusUpdateCountDown(prevCountDown => prevCountDown > 0 ? prevCountDown - 1 : 0);
        }, 1000);

        return () => clearInterval(timerInterval);
      } else {
        const timedOutPayload = {
          payment: {
            status: TRANSACTION_STATUS.EXPIRED,
            reason: 'transactionExpired'
          },
          timestamp: new Date().getTime()
        };

        updateTransactionStatus(transactionId, timedOutPayload)
          .then(() => {
            log(`transaction status updated to TIMEOUT`);
            navigate(`/gsmpi-web/webview/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.PAYMENT_TIMEOUT}});
          })
          .catch((error) => {
            log(`failed to update transaction status >> ${error}`);
            navigate(`/gsmpi-web/webview/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.PAYMENT_TIMEOUT}});
          });
      }
    }
  }, [statusUpdateCountDown]);

  useEffect(() => {
    const abortController = new AbortController();
    abortControllersRef.current.push(abortController);
    const abortSignal = abortController.signal;

    // Countdown that continuously polls the status of the transaction then does redirection based on the polled status
    if (statusPollingCountDown > 0) {
      const pollingInterval = setInterval(() => {
        setStatusPollingCountDown(prevStatusPollingCountDown => prevStatusPollingCountDown > 0 ? prevStatusPollingCountDown - 1.5 : 0);

        if (!window.navigator.onLine) {
          log(`Internet connection failure`);
          navigate(`/gsmpi-web/webview/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.INTERNET_CONNECTION_FAILURE}});
        }

        getTransactionResult(transactionId, true, abortSignal)
          .then(result => {
            abortControllersRef.current = abortControllersRef.current.filter(controller => controller !== abortController);
            if (result.data.status === TRANSACTION_STATUS.CONFIRMED) {
              clearInterval(pollingInterval);
              cancelPollingRequests();

              getPaymentCredentials({refId: result.data.refId, serviceId: serviceId})
                .then((paymentCredentialResult) => {
                  log(`success to get payment credentials`);
                  postMessageToParent({
                    type: POST_MESSAGE_TYPE.PAYMENT_RESULT,
                    content: {
                      status: PAYMENT_STATUS.SUCCESS,
                      data: paymentCredentialResult.data,
                      notificationContent: {
                        payment: {
                          service: {
                            id: serviceId
                          },
                          reference: result.data.refId,
                          status: '',
                          provider: ''
                        },
                        merchant: {
                          name: merchantName,
                        }
                      }
                    },
                    sourceOrigin: sourceOrigin
                  });
                }).catch(() => {
                  log(`failed to get payment credentials refId >> ${result.data.refId}`);
                  navigate(`/gsmpi-web/webview/${i18n.language}/error`);
                });
            } else {
              handleTransactionResult(result.data);
            }
          })
          .catch(error => {
            if (error.response.data.resultCode === 'OPM1N1042') {
              log(`Device is not supported`);
              navigate(`/gsmpi-web/webview/${i18n.language}/error`);
            } else if (error.response.data.resultCode === 'OPM2N1101' || error.response.data.resultCode === 'OPM1N1039') {
              log(`Transaction is expired`);
              navigate(`/gsmpi-web/webview/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.PAYMENT_TIMEOUT}});
            } else if (error.response.data.resultCode === 'OPM2N1103' || error.response.data.resultCode === 'OPM2N1104') {
              log(`transaction is invalid`);
              navigate(`/gsmpi-web/webview/${i18n.language}/error`);
            } else if (error.response.data.resultCode === 'OPM1N1046') {
              log(`App needs update`);
              navigate(`/gsmpi-web/webview/${i18n.language}/update`);
            }
          });
      }, 1500);

      return () => clearInterval(pollingInterval);
    }
  }, [statusPollingCountDown]);

  const cancelPollingRequests = () => {
    abortControllersRef.current.forEach(controller => controller.abort());
    abortControllersRef.current = [];
  };

  const getIntentUrl = () => {
    return `samsungpay://launch?action=aboutsamsungpay&subAction=webpayment` +
      `&transactionId=${transactionId}&serviceId=${serviceId}` +
      `#Intent;scheme=samsungpay;package=com.samsung.android.spay;end`;
  };

  const handleCloseButtonClick = () => {
    log(`closeButton has been clicked!`);
    const cancelPayload = {
      payment: {
        status: TRANSACTION_STATUS.CANCELED,
        reason: 'cancelledByUser'
      },
      timestamp: new Date().getTime()
    };

    updateTransactionStatus(transactionId, cancelPayload)
      .then(() => {
        navigate(`/gsmpi-web/webview/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.USER_CANCEL}});
      })
      .catch(() => {
        log('failed to update transaction status');
        navigate(`/gsmpi-web/webview/${i18n.language}/error`, {state: {errorType: MOBILE_ERROR_TYPE.USER_CANCEL}});
      });
  };

  const handlePayRequestClick = () => {
    log(`Handle Pay Request is not supported on webview. It will be call samsung pay app automatically.`);
  };

  return (
    <SamsungPayMobileBox mobilePageType="main" closeButtonFunction={handleCloseButtonClick}>
      <div id="payment">
        <p className={cx('txt')}>
          <span id="merchantName">{t('pay.gmspi.mobile.main.paymentRequest').replace('%s', merchantName)}</span>
        </p>
        <div className={cx('btn-block')}>
          <button type="button" className={cx('btn')} onClick={(e) => {
            if (isDisabled) {
              e.preventDefault();
            } else {
              handlePayRequestClick();
            }
          }}>
            {t('pay.gsmpi.mobile.main.button1')}
          </button>
        </div>
      </div>
    </SamsungPayMobileBox>);
}